import { db, storage, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  writeBatch,
  query,
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Utility to recursively remove undefined values (Firestore doesn't accept undefined)
const cleanFirestoreData = (obj) => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => cleanFirestoreData(item)).filter(item => item !== undefined);
  }

  const cleaned = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== undefined) {
      if (value === null) {
        cleaned[key] = null;
      } else if (typeof value === 'object') {
        cleaned[key] = cleanFirestoreData(value);
      } else {
        cleaned[key] = value;
      }
    }
  });
  return cleaned;
};

export const leitnerService = {
  async uploadQuestionNotePdf(bookId, file) {
    try {
      if (!file) {
        throw new Error('No PDF file provided');
      }
      const safeName = file.name?.replace(/[^\w.-]+/g, '_') || `note-${Date.now()}.pdf`;
      const pdfPath = `pb-books/${bookId}/question-notes/${Date.now()}-${safeName}`;
      const pdfRef = ref(storage, pdfPath);
      await uploadBytes(pdfRef, file, { contentType: file.type || 'application/pdf' });
      const url = await getDownloadURL(pdfRef);
      return {
        url,
        name: file.name || safeName,
        size: file.size || null
      };
    } catch (error) {
      console.error('Error uploading question note PDF:', error);
      throw error;
    }
  },

  async getDynamicTemplates(bookId) {
    try {
      const templatesRef = collection(db, 'pb-books', bookId, 'dynamicTemplates');
      const snapshot = await getDocs(query(templatesRef, orderBy('updatedAt', 'desc')));
      return snapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      }));
    } catch (error) {
      console.error('Error fetching dynamic templates:', error);
      throw error;
    }
  },

  async getLeitnerNotes(bookId) {
    try {
      const notesRef = collection(db, 'pb-books', bookId, 'leitner-notes');
      const snapshot = await getDocs(query(notesRef, orderBy('updatedAt', 'desc')));
      return snapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      }));
    } catch (error) {
      console.error('Error fetching leitner notes:', error);
      throw error;
    }
  },

  async addLeitnerNote(bookId, noteData) {
    try {
      const notesRef = collection(db, 'pb-books', bookId, 'leitner-notes');
      const noteRef = doc(notesRef);
      const payload = cleanFirestoreData({
        ...noteData,
        id: noteRef.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await setDoc(noteRef, payload);
      return { id: noteRef.id, ...noteData };
    } catch (error) {
      console.error('Error adding leitner note:', error);
      throw error;
    }
  },

  async updateLeitnerNote(bookId, noteId, noteData) {
    try {
      const noteRef = doc(db, 'pb-books', bookId, 'leitner-notes', noteId);
      const payload = cleanFirestoreData({
        ...noteData,
        updatedAt: serverTimestamp()
      });
      await updateDoc(noteRef, payload);
      return { id: noteId, ...noteData };
    } catch (error) {
      console.error('Error updating leitner note:', error);
      throw error;
    }
  },

  async deleteLeitnerNote(bookId, noteId) {
    try {
      const noteRef = doc(db, 'pb-books', bookId, 'leitner-notes', noteId);
      await deleteDoc(noteRef);
    } catch (error) {
      console.error('Error deleting leitner note:', error);
      throw error;
    }
  },

  async processLeitnerNotebook(bookId, content, options = {}) {
    try {
      const processCallable = httpsCallable(functions, 'processLeitnerNotebook');
      const response = await processCallable({
        bookId,
        content,
        sourceType: options.sourceType || 'text', // 'text', 'pdf', 'video'
        sourceUrl: options.sourceUrl || '',
        options: {
          generateNotes: options.generateNotes !== false,
          generateQuiz: options.generateQuiz === true,
          generateExercises: options.generateExercises === true,
          generateCards: options.generateCards === true,
          ...options
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error processing leitner notebook:', error);
      throw error;
    }
  },

  async uploadLeitnerVideo(bookId, file) {
    try {
      const videoPath = `pb-books/${bookId}/notes/videos/${Date.now()}_${file.name}`;
      const videoRef = ref(storage, videoPath);
      await uploadBytes(videoRef, file);
      const url = await getDownloadURL(videoRef);
      return { url, name: file.name };
    } catch (error) {
      console.error('Error uploading leitner video:', error);
      throw error;
    }
  },

  async saveDynamicTemplate(bookId, template) {
    try {
      const templatesRef = collection(db, 'pb-books', bookId, 'dynamicTemplates');
      const templateRef = template.id ? doc(templatesRef, template.id) : doc(templatesRef);
      const payload = cleanFirestoreData({
        ...template,
        id: templateRef.id,
        createdAt: template.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await setDoc(templateRef, payload, { merge: true });
      return { id: templateRef.id };
    } catch (error) {
      console.error('Error saving dynamic template:', error);
      throw error;
    }
  },

  async deleteDynamicTemplate(bookId, templateId) {
    try {
      const templateRef = doc(db, 'pb-books', bookId, 'dynamicTemplates', templateId);
      await deleteDoc(templateRef);
    } catch (error) {
      console.error('Error deleting dynamic template:', error);
      throw error;
    }
  },
  // Get all questions for a book
  async getBookQuestions(bookId) {
    try {
      const questionsRef = collection(db, 'pb-books', bookId, 'questions');
      const q = query(questionsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const questions = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const questionData = { id: docSnapshot.id, ...docSnapshot.data() };

          if (questionData.type === 'stem') {
            const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', docSnapshot.id, 'subquestions');
            const subQSnapshot = await getDocs(query(subQuestionsRef, orderBy('order', 'asc')));
            questionData.subQuestions = subQSnapshot.docs.map(subDoc => ({
              id: subDoc.id,
              ...subDoc.data()
            }));
          }

          return questionData;
        })
      );

      return questions;
    } catch (error) {
      console.error('Error fetching questions:', error);
      throw error;
    }
  },

  // Get learner's progress for all questions
  async getLearnerProgress(bookId, userId) {
    try {
      const progressRef = collection(db, 'pb-books', bookId, 'learnerProgress', userId, 'questions');
      const snapshot = await getDocs(progressRef);

      const progress = {};
      snapshot.docs.forEach(doc => {
        progress[doc.id] = doc.data();
      });

      // Get subquestion progress
      const subProgressRef = collection(db, 'pb-books', bookId, 'learnerProgress', userId, 'subquestions');
      const subSnapshot = await getDocs(subProgressRef);

      const subProgress = {};
      subSnapshot.docs.forEach(doc => {
        subProgress[doc.id] = doc.data();
      });

      return { questions: progress, subquestions: subProgress };
    } catch (error) {
      console.error('Error fetching learner progress:', error);
      throw error;
    }
  },

  // Initialize progress for a question (if not exists)
  async initializeQuestionProgress(bookId, userId, questionId, isSubQuestion = false, parentQuestionId = null) {
    try {
      const progressPath = isSubQuestion
        ? `pb-books/${bookId}/learnerProgress/${userId}/subquestions/${questionId}`
        : `pb-books/${bookId}/learnerProgress/${userId}/questions/${questionId}`;

      const progressRef = doc(db, progressPath);
      const progressDoc = await getDoc(progressRef);

      if (!progressDoc.exists()) {
        await setDoc(progressRef, {
          box: 1,
          attempts: 0,
          correctAttempts: 0,
          lastReviewed: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(isSubQuestion && { parentQuestionId })
        });
      }
    } catch (error) {
      console.error('Error initializing question progress:', error);
      throw error;
    }
  },

  // Get questions with learner progress merged
  async getQuestionsWithProgress(bookId, userId) {
    try {
      const questions = await this.getBookQuestions(bookId);
      const progress = await this.getLearnerProgress(bookId, userId);

      return questions.map(q => {
        if (q.type === 'simple') {
          const questionProgress = progress.questions[q.id] || { box: 1, attempts: 0, correctAttempts: 0 };
          return { ...q, ...questionProgress };
        } else if (q.type === 'stem') {
          const subQuestionsWithProgress = (q.subQuestions || []).map(subQ => {
            const subProgress = progress.subquestions[subQ.id] || { box: 1, attempts: 0, correctAttempts: 0 };
            return { ...subQ, ...subProgress };
          });
          return { ...q, subQuestions: subQuestionsWithProgress };
        }
        return q;
      });
    } catch (error) {
      console.error('Error getting questions with progress:', error);
      throw error;
    }
  },

  // Helper to upload hint media
  async uploadHintMedia(bookId, questionId, hint) {
    if (!hint) return null;

    const processedHint = { ...hint };

    // Upload audio if blob exists
    if (hint.audio?.blob) {
      const audioPath = `pb-books/${bookId}/questions/${questionId}/hint-audio-${Date.now()}.webm`;
      const audioRef = ref(storage, audioPath);
      await uploadBytes(audioRef, hint.audio.blob, { contentType: 'audio/webm' });
      processedHint.audio = {
        url: await getDownloadURL(audioRef),
        duration: hint.audio.duration,
        speaker: hint.audio.speaker
      };
    }

    // Upload video if file exists
    if (hint.video?.file) {
      const videoPath = `pb-books/${bookId}/questions/${questionId}/hint-video-${Date.now()}-${hint.video.name}`;
      const videoRef = ref(storage, videoPath);
      await uploadBytes(videoRef, hint.video.file);
      processedHint.video = {
        type: 'upload',
        url: await getDownloadURL(videoRef),
        name: hint.video.name
      };
    }

    return processedHint;
  },

  async processQuestionMedia(bookId, questionId, media) {
    if (!media || media.type === 'none') {
      return { type: 'none', images: [], video: null, embed: null };
    }

    const processedMedia = { ...media };

    // Process images
    if (media.type === 'images' && media.images?.length > 0) {
      const uploadedImages = await Promise.all(
        media.images.map(async (img, index) => {
          if (img instanceof File) {
            const imagePath = `pb-books/${bookId}/questions/${questionId}/media/${Date.now()}_${index}_${img.name}`;
            const imageRef = ref(storage, imagePath);
            await uploadBytes(imageRef, img);
            const url = await getDownloadURL(imageRef);
            return { url, name: img.name };
          }
          return img;
        })
      );
      processedMedia.images = uploadedImages;
    }

    // Process video upload
    if (media.type === 'video' && media.video?.source === 'upload' && media.video?.file instanceof File) {
      const videoPath = `pb-books/${bookId}/questions/${questionId}/media/${Date.now()}_${media.video.file.name}`;
      const videoRef = ref(storage, videoPath);
      await uploadBytes(videoRef, media.video.file);
      const url = await getDownloadURL(videoRef);
      processedMedia.video = {
        source: 'upload',
        url,
        name: media.video.name
      };
    }

    // YouTube videos and embeds don't need processing
    return processedMedia;
  },

  // Add a new question
  async addQuestion(bookId, questionData, imageFile = null, userId = null) {
    try {
      let imageUrl = null;

      if (imageFile) {
        const imagePath = `pb-books/${bookId}/questions/${Date.now()}_${imageFile.name}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      let processedMedia = null;
      if (questionData.media && questionData.media.type !== 'none') {
        const tempId = Date.now().toString();
        processedMedia = await this.processQuestionMedia(bookId, tempId, questionData.media);
      }

      const { subQuestions, hint, ...mainQuestionData } = questionData;

      // Clean data first, then add timestamps
      const questionToAdd = cleanFirestoreData({
        ...mainQuestionData,
        ...(questionData.type === 'simple' ? { image: imageUrl } : { stemImage: imageUrl }),
        userId: userId,
        hint: null,
        media: processedMedia || { type: 'none', images: [], video: null, embed: null }
      });

      // Add timestamps AFTER cleaning
      questionToAdd.createdAt = serverTimestamp();
      questionToAdd.updatedAt = serverTimestamp();

      const questionsRef = collection(db, 'pb-books', bookId, 'questions');
      const docRef = await addDoc(questionsRef, questionToAdd);

      // Upload hint media if present
      if (hint) {
        const processedHint = await this.uploadHintMedia(bookId, docRef.id, hint);
        if (processedHint) {
          await updateDoc(doc(db, 'pb-books', bookId, 'questions', docRef.id), { hint: processedHint });
          questionToAdd.hint = processedHint;
        }
      }

      if (questionData.type === 'stem' && subQuestions && subQuestions.length > 0) {
        const batch = writeBatch(db);
        const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', docRef.id, 'subquestions');

        subQuestions.forEach((subQ, index) => {
          const subQRef = doc(subQuestionsRef);
          const cleanedSubQ = cleanFirestoreData({
            ...subQ,
            order: index
          });
          // Add timestamps after cleaning
          cleanedSubQ.createdAt = serverTimestamp();
          cleanedSubQ.updatedAt = serverTimestamp();
          batch.set(subQRef, cleanedSubQ);
        });

        await batch.commit();

        const createdSubQs = await getDocs(query(subQuestionsRef, orderBy('order', 'asc')));
        questionToAdd.subQuestions = createdSubQs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      const bookRef = doc(db, 'pb-books', bookId);
      const bookDoc = await getDoc(bookRef);
      const currentCount = bookDoc.data()?.questionCount || 0;

      const countIncrement = questionData.type === 'stem'
        ? (subQuestions?.length || 0)
        : 1;

      await updateDoc(bookRef, {
        questionCount: currentCount + countIncrement,
        updatedAt: serverTimestamp()
      });

      return { id: docRef.id, ...questionToAdd };
    } catch (error) {
      console.error('Error adding question:', error);
      throw error;
    }
  },

  async updateQuestion(bookId, questionId, questionData, imageFile = undefined, existingImageUrl = null, userId = null) {
    try {
      let imageUrl = existingImageUrl;
      const imageField = questionData.type === 'simple' ? 'image' : 'stemImage';

      // Only process if imageFile is explicitly provided
      if (imageFile instanceof File) {
        // Delete old image if exists
        if (existingImageUrl) {
          try {
            const oldImageRef = ref(storage, existingImageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.log('Old image not found or already deleted');
          }
        }

        // Upload new image
        const imagePath = `pb-books/${bookId}/questions/${Date.now()}_${imageFile.name}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      } else if (imageFile === null) {
        // Explicit deletion request - remove image
        if (existingImageUrl) {
          try {
            const oldImageRef = ref(storage, existingImageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.log('Image not found or already deleted');
          }
        }
        imageUrl = null;
      }

      let processedMedia = questionData.media;
      if (questionData.media !== undefined) {
        if (questionData.media && questionData.media.type !== 'none') {
          processedMedia = await this.processQuestionMedia(bookId, questionId, questionData.media);
        } else {
          processedMedia = { type: 'none', images: [], video: null, embed: null };
        }
      }

      const { subQuestions, hint, ...mainQuestionData } = questionData;

      // Process hint media
      let processedHint = hint;
      if (hint && (hint.audio?.blob || hint.video?.file)) {
        processedHint = await this.uploadHintMedia(bookId, questionId, hint);
      }

      const questionRef = doc(db, 'pb-books', bookId, 'questions', questionId);

      // Clean data first
      const updateData = cleanFirestoreData({
        ...mainQuestionData,
        [imageField]: imageUrl,
        userId: userId,
        hint: processedHint,
        media: processedMedia
      });

      // Add timestamp AFTER cleaning
      updateData.updatedAt = serverTimestamp();

      await updateDoc(questionRef, updateData);

      if (questionData.type === 'stem' && subQuestions) {
        const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', questionId, 'subquestions');

        const existingSubQs = await getDocs(subQuestionsRef);
        const batch = writeBatch(db);
        existingSubQs.docs.forEach(d => batch.delete(d.ref));

        subQuestions.forEach((subQ, index) => {
          const subQRef = doc(subQuestionsRef);
          const cleanedSubQ = cleanFirestoreData({
            ...subQ,
            order: index
          });
          // Add timestamp after cleaning
          cleanedSubQ.updatedAt = serverTimestamp();
          batch.set(subQRef, cleanedSubQ);
        });

        await batch.commit();

        const updatedSubQs = await getDocs(query(subQuestionsRef, orderBy('order', 'asc')));
        updateData.subQuestions = updatedSubQs.docs.map(d => ({ id: d.id, ...d.data() }));
      }

      return { id: questionId, ...updateData };
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  },


  // Delete a question
  async deleteQuestion(bookId, questionId, imageUrl = null) {
    try {
      if (imageUrl) {
        try {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.log('Image not found or already deleted');
        }
      }

      const questionRef = doc(db, 'pb-books', bookId, 'questions', questionId);
      const questionDoc = await getDoc(questionRef);
      const questionData = questionDoc.data();

      let deletedCount = 1;
      if (questionData?.type === 'stem') {
        const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', questionId, 'subquestions');
        const subQSnapshot = await getDocs(subQuestionsRef);
        deletedCount = subQSnapshot.size;

        const batch = writeBatch(db);
        subQSnapshot.docs.forEach(doc => batch.delete(doc.ref));
        await batch.commit();
      }

      await deleteDoc(questionRef);

      const bookRef = doc(db, 'pb-books', bookId);
      const bookDoc = await getDoc(bookRef);
      const currentCount = bookDoc.data()?.questionCount || 0;

      await updateDoc(bookRef, {
        questionCount: Math.max(0, currentCount - deletedCount),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  },

  // Update learner's progress for a question
  async updateLearnerProgress(bookId, userId, questionId, isCorrect, isSubQuestion = false, parentQuestionId = null) {
    const learnerRootRef = doc(
      db,
      'pb-books',
      bookId,
      'learnerProgress',
      userId
    );

    await setDoc(
      learnerRootRef,
      {
        userId,
        startedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );

    try {
      const progressPath = isSubQuestion
        ? `pb-books/${bookId}/learnerProgress/${userId}/subquestions/${questionId}`
        : `pb-books/${bookId}/learnerProgress/${userId}/questions/${questionId}`;

      const progressRef = doc(db, progressPath);
      const progressDoc = await getDoc(progressRef);

      let currentBox = 1;
      let attempts = 0;
      let correctAttempts = 0;

      if (progressDoc.exists()) {
        const data = progressDoc.data();
        currentBox = data.box || 1;
        attempts = data.attempts || 0;
        correctAttempts = data.correctAttempts || 0;
      }

      // Determine next box
      let nextBox = currentBox;
      if (isCorrect) {
        switch (currentBox) {
          case 1: nextBox = 2; break;
          case 2: nextBox = 3; break;
          case 3: nextBox = 4; break;
          default: nextBox = 4;
        }
      } else {
        nextBox = 1; // Move back to box 1 on incorrect answer
      }

      const updateData = {
        box: nextBox,
        attempts: attempts + 1,
        correctAttempts: isCorrect ? correctAttempts + 1 : correctAttempts,
        lastReviewed: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(isSubQuestion && !progressDoc.exists() && { parentQuestionId })
      };

      await setDoc(progressRef, updateData, { merge: true });

      return { box: nextBox };
    } catch (error) {
      console.error('Error updating learner progress:', error);
      throw error;
    }
  },

  // Batch update learner progress (for test completion)
  async batchUpdateLearnerProgress(bookId, userId, updates) {
    try {
      const batch = writeBatch(db);

      for (const update of updates) {
        const progressPath = update.isSubQuestion
          ? `pb-books/${bookId}/learnerProgress/${userId}/subquestions/${update.questionId}`
          : `pb-books/${bookId}/learnerProgress/${userId}/questions/${update.questionId}`;

        const progressRef = doc(db, progressPath);

        batch.set(progressRef, {
          box: update.newBox,
          lastReviewed: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(update.isSubQuestion && { parentQuestionId: update.parentQuestionId })
        }, { merge: true });
      }

      await batch.commit();
    } catch (error) {
      console.error('Error batch updating learner progress:', error);
      throw error;
    }
  },

  // Get box statistics for a learner
  async getLearnerBoxStats(bookId, userId) {
    try {
      const progress = await this.getLearnerProgress(bookId, userId);

      const stats = { 1: 0, 2: 0, 3: 0, 4: 0 };

      Object.values(progress.questions).forEach(p => {
        if (p.box >= 1 && p.box <= 4) {
          stats[p.box]++;
        }
      });

      Object.values(progress.subquestions).forEach(p => {
        if (p.box >= 1 && p.box <= 4) {
          stats[p.box]++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting learner box stats:', error);
      throw error;
    }
  },

  // Get tests for a book
  async getTests(bookId) {
    try {
      const testsRef = collection(db, 'pb-books', bookId, 'tests');
      const q = query(testsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching tests:', error);
      throw error;
    }
  },

  // Add a test
  async addTest(bookId, testData) {
    try {
      const testsRef = collection(db, 'pb-books', bookId, 'tests');

      const testToAdd = {
        name: testData.name,
        instructions: testData.instructions || null,
        questionIds: testData.questionIds,
        timeLimitPerQuestion: testData.timeLimitPerQuestion || 0,
        passingScore: testData.passingScore || null,
        totalMarks: testData.totalMarks || testData.questionIds.length,
        questionCount: testData.questionCount || testData.questionIds.length,
        // Categories
        subject: testData.subject || null,
        grade: testData.grade || null,
        topics: testData.topics || [],
        // Scheduling
        schedule: testData.schedule || null,
        // Metadata
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(testsRef, testToAdd);

      return {
        id: docRef.id,
        ...testToAdd,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error adding test:', error);
      throw error;
    }
  },

  // Add method to get available tests (respects scheduling)
  async getAvailableTests(bookId) {
    try {
      const tests = await this.getTests(bookId);
      const now = new Date();

      return tests.filter(test => {
        if (!test.schedule?.isScheduled) return true;

        const startTime = new Date(test.schedule.startDateTime);
        const endTime = test.schedule.endDateTime ? new Date(test.schedule.endDateTime) : null;

        if (now < startTime) return false;
        if (endTime && now > endTime) return false;

        return true;
      });
    } catch (error) {
      console.error('Error getting available tests:', error);
      throw error;
    }
  },

  // Delete a test
  async deleteTest(bookId, testId) {
    try {
      const testRef = doc(db, 'pb-books', bookId, 'tests', testId);
      await deleteDoc(testRef);

      const bookRef = doc(db, 'pb-books', bookId);
      const bookDoc = await getDoc(bookRef);
      const currentCount = bookDoc.data()?.testCount || 0;

      await updateDoc(bookRef, {
        testCount: Math.max(0, currentCount - 1),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting test:', error);
      throw error;
    }
  },

  // Save test result
  async saveTestResult(bookId, result) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'testResults');

      const resultToSave = {
        testId: result.testId,
        testName: result.testName,
        userId: result.userId || null,
        score: result.score,
        totalQuestions: result.totalQuestions,
        totalMarks: result.totalMarks || result.score,
        maxMarks: result.maxMarks || result.totalQuestions,
        percentage: result.totalQuestions > 0
          ? Math.round((result.score / result.totalQuestions) * 100)
          : 0,
        date: result.date || Date.now(),
        completedAt: result.completedAt || serverTimestamp(),
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(resultsRef, resultToSave);

      return { id: docRef.id, ...resultToSave };
    } catch (error) {
      console.error('Error saving test result:', error);
      throw error;
    }
  },

  // Get all test results for a book
  async getTestResults(bookId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'testResults');
      const q = query(resultsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching test results:', error);
      throw error;
    }
  },

  // Get user's test results
  async getUserTestResults(bookId, userId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'testResults');
      const q = query(resultsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching test results:', error);
      throw error;
    }
  },

  async getQuestionCategories(bookId) {
    try {
      const questions = await this.getBookQuestions(bookId);

      const subjects = new Set();
      const grades = new Set();
      const topics = new Set();
      const subtopics = new Set();

      questions.forEach(q => {
        if (q.subject) subjects.add(q.subject);

        // Handle both old single grade and new multi-grade format
        if (Array.isArray(q.grades)) {
          q.grades.forEach(g => grades.add(g));
        } else if (q.grade) {
          grades.add(q.grade);
        }

        if (q.topics && Array.isArray(q.topics)) {
          q.topics.forEach(topic => topics.add(topic));
        }

        // Collect subtopics
        if (q.subtopics && Array.isArray(q.subtopics)) {
          q.subtopics.forEach(subtopic => subtopics.add(subtopic));
        }

        // Handle stem questions
        if (q.type === 'stem' && q.subQuestions) {
          q.subQuestions.forEach(subQ => {
            if (subQ.topics && Array.isArray(subQ.topics)) {
              subQ.topics.forEach(topic => topics.add(topic));
            }
            if (subQ.subtopics && Array.isArray(subQ.subtopics)) {
              subQ.subtopics.forEach(subtopic => subtopics.add(subtopic));
            }
          });
        }
      });

      return {
        subjects: Array.from(subjects).sort(),
        grades: Array.from(grades).sort(),
        topics: Array.from(topics).sort(),
        subtopics: Array.from(subtopics).sort()
      };
    } catch (error) {
      console.error('Error fetching question categories:', error);
      throw error;
    }
  },

  // Voice Guides Methods
  async getVoiceGuides(bookId) {
    try {
      const guidesRef = collection(db, 'pb-books', bookId, 'voiceGuides');
      const q = query(guidesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching voice guides:', error);
      return [];
    }
  },

  async addVoiceGuide(bookId, voiceData) {
    try {
      const { audioBlob, duration, speaker, title } = voiceData;

      if (!audioBlob) {
        throw new Error('No audio blob provided');
      }

      const mimeType = audioBlob.type || 'audio/webm';
      const audioPath = `pb-books/${bookId}/voice-guides/${Date.now()}.webm`;
      const storageRef = ref(storage, audioPath);

      await uploadBytes(storageRef, audioBlob, { contentType: mimeType });
      const url = await getDownloadURL(storageRef);

      const guidesRef = collection(db, 'pb-books', bookId, 'voiceGuides');
      const guideDoc = await addDoc(guidesRef, {
        title,
        url,
        duration,
        speaker,
        mimeType,
        createdAt: serverTimestamp()
      });

      return {
        id: guideDoc.id,
        title,
        url,
        duration,
        speaker,
        mimeType,
        createdAt: new Date()
      };
    } catch (error) {
      console.error('Error adding voice guide:', error);
      throw error;
    }
  },

  async deleteVoiceGuide(bookId, guideId) {
    try {
      // Get guide to find storage URL
      const guideRef = doc(db, 'pb-books', bookId, 'voiceGuides', guideId);
      const guideSnap = await getDoc(guideRef);

      if (guideSnap.exists()) {
        const guideData = guideSnap.data();

        // Delete from storage if URL exists
        if (guideData.url) {
          try {
            const storageRef = ref(storage, guideData.url);
            await deleteObject(storageRef);
          } catch (storageError) {
            console.warn('Could not delete storage file:', storageError);
          }
        }
      }

      // Delete from Firestore
      await deleteDoc(guideRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting voice guide:', error);
      throw error;
    }
  },

  // Filter questions by categories
  filterQuestionsByCategories(questions, filters) {
    if (!filters.subject &&
      (!filters.grades || filters.grades.length === 0) &&
      (!filters.grade) &&
      (!filters.topics || filters.topics.length === 0)) {
      return questions;
    }

    const filterGrades = filters.grades || (filters.grade ? [filters.grade] : []);

    return questions.filter(q => {
      let matches = true;

      if (filters.subject && q.subject !== filters.subject) {
        matches = false;
      }

      // Handle multi-grade filtering
      if (filterGrades.length > 0) {
        const questionGrades = Array.isArray(q.grades) ? q.grades : (q.grade ? [q.grade] : []);
        const hasMatchingGrade = filterGrades.some(g => questionGrades.includes(g));
        if (!hasMatchingGrade) {
          matches = false;
        }
      }

      if (filters.topics && filters.topics.length > 0) {
        const questionTopics = q.topics || [];
        const hasMatchingTopic = filters.topics.some(topic => questionTopics.includes(topic));
        if (!hasMatchingTopic) {
          matches = false;
        }
      }

      return matches;
    });
  },

  // Get all learners with their progress
  async getAllLearnersProgress(bookId) {
    try {
      const learnerProgressRef =
        collection(db, 'pb-books', bookId, 'learnerProgress');

      const snapshot = await getDocs(learnerProgressRef);

      const learnersProgress = await Promise.all(
        snapshot.docs.map(async (learnerDoc) => {
          const userId = learnerDoc.id;

          const { questions = {}, subquestions = {} } =
            await this.getLearnerProgress(bookId, userId);

          return {
            userId,
            questions: Object.entries(questions).map(
              ([id, data]) => ({ id, ...data })
            ),
            subquestions: Object.entries(subquestions).map(
              ([id, data]) => ({ id, ...data })
            )
          };
        })
      );

      return learnersProgress;
    } catch (error) {
      console.error('Error fetching all learners progress:', error);
      throw error;
    }
  },

  // Get current week boundaries (Mon-Fri practice, Sat-Sun test)
  getCurrentWeekBoundaries() {
    const now = new Date();
    const dayOfWeek = now.getDay();

    const monday = new Date(now);
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    monday.setDate(now.getDate() - daysFromMonday);
    monday.setHours(0, 0, 0, 0);

    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);

    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);
    saturday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return { monday, friday, saturday, sunday, isWeekend: dayOfWeek === 0 || dayOfWeek === 6 };
  },

  // Calculate weekly stats from progress items
  getWeeklyStats(items, monday, friday) {
    const stats = { answered: 0, correct: 0, byDay: {} };

    // Initialize Mon-Fri
    for (let i = 0; i < 5; i++) {
      const day = new Date(monday);
      day.setDate(day.getDate() + i);
      stats.byDay[day.toISOString().split('T')[0]] = { answered: 0, correct: 0 };
    }

    items.forEach(item => {
      if (!item.lastReviewed) return;
      const reviewDate = item.lastReviewed.toDate ? item.lastReviewed.toDate() : new Date(item.lastReviewed);

      if (reviewDate >= monday && reviewDate <= friday) {
        const dayKey = reviewDate.toISOString().split('T')[0];
        if (stats.byDay[dayKey]) {
          stats.byDay[dayKey].answered++;
          stats.answered++;
          if (item.box > 1) {
            stats.byDay[dayKey].correct++;
            stats.correct++;
          }
        }
      }
    });

    return stats;
  },

  async getQuestionMetaMap(bookId) {
    const questionsRef = collection(db, 'pb-books', bookId, 'questions');
    const snapshot = await getDocs(questionsRef);

    const map = {};

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      map[doc.id] = {
        subject: data.subject || null,
        grade: data.grade || null,
        topics: data.topics || [],
        type: data.type
      };
    });

    return map;
  }

};
